/**
 * Clinic slice — the clinic the user is CURRENTLY "in".
 *
 * Session 18 feature. The post-login flow lets the user choose between
 * the Personal panel (Dashboard) and "entering a clinic". When they
 * enter a clinic (by typing/scanning its 6-digit code), we resolve the
 * clinic name and stash it here so the Dashboard can show a "you are
 * currently at {clinic}" banner and offer a "change clinic" button.
 *
 * Why Redux (client state), not TanStack Query (server state):
 *   The CURRENT clinic is a per-session UI selection, not data fetched
 *   from the backend. The backend has no notion of "the clinic the user
 *   is looking at right now" — checkout and clinic-tickets each pass a
 *   clinicCode explicitly per request. So this is purely client state.
 *
 * Why NOT persisted to localStorage (unlike language):
 *   It mirrors the auth model — the access token lives in memory only
 *   and a page reload bounces the user to /login (tech debt #4). A
 *   clinic selection that outlived the session would be stale, so we
 *   keep it in memory and clear it on logout.
 *
 * Shape of `currentClinic`:
 *   { code: "123456", name: { fa: "...", en: "..." } }
 *   `name` is the bilingual object the resolve endpoint returns, so the
 *   Dashboard banner can switch language instantly via `localized()`.
 *   `null` means "no clinic entered yet" (Personal-panel-only session).
 */

import { createSlice } from '@reduxjs/toolkit';
import { logout } from '@/store/authSlice';

/** @type {{ currentClinic: { code: string, name: (string|{fa?: string, en?: string}) } | null }} */
const initialState = {
  currentClinic: null,
};

const clinicSlice = createSlice({
  name: 'clinic',
  initialState,
  reducers: {
    /**
     * Set the clinic the user has just entered.
     * @param {{ payload: { code: string, name: (string|{fa?: string, en?: string}), capturePolicy?: string } }} action
     */
    setCurrentClinic(state, action) {
      state.currentClinic = action.payload;
    },

    /** Forget the current clinic (e.g., user backs out of a clinic context). */
    clearCurrentClinic(state) {
      state.currentClinic = null;
    },
  },
  // Clear the selection whenever auth is cleared (logout / inactivity /
  // refresh failure) so a new login never inherits a stale clinic.
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.currentClinic = null;
    });
  },
});

export const { setCurrentClinic, clearCurrentClinic } = clinicSlice.actions;

export default clinicSlice.reducer;

// Selectors — use these instead of reading state directly.
export const selectCurrentClinic = (state) => state.clinic.currentClinic;
export const selectHasCurrentClinic = (state) => state.clinic.currentClinic != null;
